import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CustomDropdown from '../Common/CustomDropdown';
import {
  BlurBackgroundIcon,
  MicOffIcon,
  MicOnIcon,
  NewUpdateIcon,
  VolumeIcon,
  VolumeOffIcon,
} from '../Common/Icons';
import {
  useAudioInputs,
  useAudioOutputs,
  useAudioVideo,
  useLocalVideo,
  useToggleLocalMute,
  useVideoInputs,
} from 'amazon-chime-sdk-component-library-react';
import {
  BackgroundBlurVideoFrameProcessor,
  BackgroundReplacementVideoFrameProcessor,
  DefaultVideoTransformDevice,
} from 'amazon-chime-sdk-js';
import ProgressBar from '../Common/ProgressBar';
import { useDispatch, useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { updateCallSettings } from '@vantiva-sa/homesight_humaui/Store/videoCallStore';
import { getUserConfig } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import LocalStorage from '@vantiva-sa/homesight_humaui/Utilities/LocalStorage';
import { VideoCallBackgroundEffect } from '../../constants/videoCall';

const getOptions = (options) =>
  (options || []).map((a) => {
    const fullLabel = a.label || '';
    const truncated = fullLabel.length > 35 ? fullLabel.slice(0, 35) + '...' : fullLabel;
    return {
      key: a.deviceId,
      label: truncated,
      renderLabel: <span title={fullLabel}>{truncated}</span>,
    };
  });

const getVolume = () => {
  const audioElements = document.querySelectorAll('audio') ?? [];
  return audioElements[0]?.volume ?? 1;
};

const VideoCallSettings = ({
  isSetupDone,
  localChimeUser,
  showSelfView,
  setShowSelfView,
  isBootstrapOnly = false,
  setIsBlurEnabled,
  isBlurEnabled,
  isReplacementEnabled,
  setIsReplacementEnabled,
  customBackgroundImage,
  setSelectedCustomBackground,
  selectedCustomBackground,
  shouldShowProviderBackground,
  lastEffectRef,
  onProvideHandlers,
  onApplyingStateChange,
}) => {
  const trans = useSelector(translator);
  const dispatch = useDispatch();
  const currentUser = useSelector(getCurrentUser);
  const callSettings = useSelector((state) => state.videoCall.callSettings);
  const audioVideo = useAudioVideo();
  const { isVideoEnabled } = useLocalVideo();
  const { muted } = useToggleLocalMute();
  const { devices: videoDevices, selectedDevice: selVideoDevice } = useVideoInputs();
  const { devices: audioInDevices, selectedDevice: selAudioInDevice } = useAudioInputs();
  const { devices: audioOutDevices, selectedDevice: selAudioOutDevice } = useAudioOutputs();

  const [selectedVideoDevice, setSelectedVideoDevice] = useState(
    callSettings.videoDevice || selVideoDevice
  );
  const [selectedAudioOutDevice, setSelectedAudioOutDevice] = useState(
    callSettings.audioOutputDevice || selAudioOutDevice
  );
  const [selectedAudioInDevice, setSelectedAudioInDevice] = useState(
    callSettings.audioInputDevice || selAudioInDevice
  );
  const [volumeLevel, setVolumeLevel] = useState(getVolume());
  const [audioInStrength, setAudioInStrength] = useState(0);

  const [blurSupported, setBlurSupported] = useState(false);
  const [transformDevice, setTransformDevice] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyingEffect, setApplyingEffect] = useState(null);

  const [selectedEffect, setSelectedEffect] = useState(() => {
    const useruuid = currentUser?.useruuid;
    if (!useruuid) return VideoCallBackgroundEffect.none;
    const saved = LocalStorage.get(AppStorageKeys.videocallSettings);
    const allSettings = saved && typeof saved === 'object' ? saved : {};
    const userSettings = allSettings[useruuid] || {};
    return userSettings.backgroundEffect || VideoCallBackgroundEffect.none;
  });
  const didRestoreStoredEffect = useRef(false);
  const restoredForUserRef = useRef(null);
  const effectHandlersRef = useRef({
    handleBlurToggle: null,
    applyBackgroundReplacement: null,
    revertBackground: null,
  });

  const loggedInUserUUID = currentUser?.useruuid;

  const getStoredVideoCallSettings = () => {
    const saved = LocalStorage.get(AppStorageKeys.videocallSettings);
    return saved && typeof saved === 'object' ? saved : {};
  };

  const getStoredEffectForUser = (useruuid) => {
    if (!useruuid) return VideoCallBackgroundEffect.none;
    const allSettings = getStoredVideoCallSettings();
    const userSettings = allSettings[useruuid] || {};
    return userSettings.backgroundEffect || VideoCallBackgroundEffect.none;
  };

  const saveEffectForUser = (effect) => {
    if (!loggedInUserUUID) return;
    const allSettings = getStoredVideoCallSettings();
    allSettings[loggedInUserUUID] = {
      ...(allSettings[loggedInUserUUID] || {}),
      backgroundEffect: effect,
    };
    LocalStorage.set(AppStorageKeys.videocallSettings, allSettings);
  };

  useLayoutEffect(() => {
    if (restoredForUserRef.current !== loggedInUserUUID) {
      restoredForUserRef.current = loggedInUserUUID;
      didRestoreStoredEffect.current = false;
      setSelectedEffect(getStoredEffectForUser(loggedInUserUUID));
    }
  }, [loggedInUserUUID]);

  useEffect(() => {
    if (isApplying) return;

    if (isBlurEnabled) {
      setSelectedEffect(VideoCallBackgroundEffect.blur);
    } else if (isReplacementEnabled && selectedCustomBackground) {
      setSelectedEffect(VideoCallBackgroundEffect.background);
    } else {
      setSelectedEffect(VideoCallBackgroundEffect.none);
    }
  }, [isApplying, isBlurEnabled, isReplacementEnabled, selectedCustomBackground]);

  useEffect(() => {
    if (didRestoreStoredEffect.current) return;
    if (!isSetupDone || !isVideoEnabled || !loggedInUserUUID || !audioVideo || isApplying) return;

    const restoreStoredEffect = async () => {
      const storedEffect = getStoredEffectForUser(loggedInUserUUID);

      if (storedEffect === VideoCallBackgroundEffect.blur) {
        setSelectedEffect(VideoCallBackgroundEffect.blur);
        if (isBlurEnabled) {
          didRestoreStoredEffect.current = true;
          return;
        }
        if (!blurSupported || !selectedVideoDevice) return;
        await handleBlurToggle(true);
        didRestoreStoredEffect.current = true;
        return;
      }

      if (storedEffect === VideoCallBackgroundEffect.background) {
        setSelectedEffect(VideoCallBackgroundEffect.background);
        if (isReplacementEnabled && selectedCustomBackground === customBackgroundImage) {
          didRestoreStoredEffect.current = true;
          return;
        }
        if (!shouldShowProviderBackground || !customBackgroundImage || !selectedVideoDevice) {
          // Wait for provider background resource/device readiness.
          return;
        }
        setSelectedCustomBackground(customBackgroundImage);
        await applyBackgroundReplacement(customBackgroundImage);
        didRestoreStoredEffect.current = true;
        return;
      }

      didRestoreStoredEffect.current = true;
    };

    restoreStoredEffect();
  }, [
    isSetupDone,
    isVideoEnabled,
    loggedInUserUUID,
    audioVideo,
    isApplying,
    blurSupported,
    selectedVideoDevice,
    shouldShowProviderBackground,
    customBackgroundImage,
    isBlurEnabled,
    isReplacementEnabled,
    selectedCustomBackground,
  ]);

  useEffect(() => {
    if (onApplyingStateChange) {
      onApplyingStateChange(isApplying, applyingEffect);
    }
  }, [isApplying, applyingEffect]);

  const currentUserConfig = useSelector(getUserConfig(localChimeUser?.useruuid));
  const blurPercentage = parseInt(
    currentUserConfig?.homesight_videocall?.background_blur_percentage ?? '50',
    10
  );

  const isCustomBackgroundSelected =
    isReplacementEnabled && selectedCustomBackground === customBackgroundImage && !isApplying;
  const isNoneApplying = isApplying && selectedEffect === VideoCallBackgroundEffect.none;
  const isBlurApplying = isApplying && selectedEffect === VideoCallBackgroundEffect.blur;
  const isBackgroundApplying =
    isApplying && selectedEffect === VideoCallBackgroundEffect.background;

  useEffect(() => {
    const checkSupport = async () => {
      const supported = await BackgroundBlurVideoFrameProcessor.isSupported();
      setBlurSupported(supported);
    };
    checkSupport();
  }, []);

  const getBlurStrengthFromPercentage = (percentage) => {
    // Amazon Chime recommends values from 1 (least) to 20 (strongest)
    const min = 1; //least blur
    const max = 20; //most blur
    const clamped = Math.min(Math.max(percentage, 0), 100);
    return Math.round((clamped / 100) * (max - min) + min);
  };

  useEffect(() => {
    if (!localChimeUser?.chimeAttendeeId) return;
    audioVideo?.realtimeSubscribeToVolumeIndicator(localChimeUser?.chimeAttendeeId, (_, volume) =>
      setAudioInStrength((volume ?? 0) * 100)
    );
    return () =>
      audioVideo?.realtimeUnsubscribeFromVolumeIndicator(localChimeUser?.chimeAttendeeId);
  }, [audioVideo, localChimeUser]);

  useEffect(() => {
    const audioElements = document.querySelectorAll('audio') ?? [];
    if (!audioElements.length) return;
    const element = audioElements[0];
    element.volume = volumeLevel;
  }, [volumeLevel]);

  const handleVideoDeviceChange = (device) => {
    try {
      const key = device || '';
      if (!key) return;
      audioVideo?.startVideoInput(key).then(() => {
        setSelectedVideoDevice(key);
        dispatch(updateCallSettings({ videoDevice: key }));
      });
    } catch (error) {
      console.error('Error changing Video device:', error);
    }
  };
  const handleAudioOutputChange = (device) => {
    try {
      const key = device || '';
      if (!key) return;
      audioVideo?.chooseAudioOutput(key).then(() => {
        setSelectedAudioOutDevice(key);
        dispatch(updateCallSettings({ audioOutputDevice: key }));
      });
    } catch (error) {
      console.error('Error changing Audio device:', error);
    }
  };
  const handleAudioInputChange = (device) => {
    try {
      const key = device || '';
      if (!key) return;
      audioVideo?.startAudioInput(key).then(() => {
        setSelectedAudioInDevice(key);
        dispatch(updateCallSettings({ audioInputDevice: key }));
      });
    } catch (error) {
      console.error('Error changing Microphone device:', error);
    }
  };

  const handleBlurToggle = async (enable, effect) => {
    try {
      setIsApplying(true);
      setApplyingEffect(VideoCallBackgroundEffect.blur);
      if (!blurSupported || !selectedVideoDevice) {
        return;
      }
      if (enable && isBlurEnabled && transformDevice) {
        await audioVideo?.startVideoInput(transformDevice);
        audioVideo?.startLocalVideoTile();
        return;
      }
      if (!enable && !isBlurEnabled) {
        return;
      }
      if (enable && setSelectedCustomBackground) {
        setSelectedCustomBackground(null);
      }
      if (enable) {
        const strength = getBlurStrengthFromPercentage(blurPercentage);
        const processor = await BackgroundBlurVideoFrameProcessor.create(null, {
          blurStrength: strength,
          useCpuCapture: true,
        });
        await processor.setBlurStrength(strength);
        const blurDevice = new DefaultVideoTransformDevice(console, selectedVideoDevice, [
          processor,
        ]);
        await audioVideo?.startVideoInput(blurDevice);
        audioVideo.startLocalVideoTile();
        setTransformDevice(blurDevice);
      } else {
        if (transformDevice?.stop) {
          transformDevice.stop();
        }
        await audioVideo?.startVideoInput(selectedVideoDevice);
        audioVideo.startLocalVideoTile();
        setTransformDevice(null);
      }
      setIsBlurEnabled(enable);
      if (enable) {
        setIsReplacementEnabled(false);
      }
      if (enable) {
        saveEffectForUser(VideoCallBackgroundEffect.blur);
      }
    } catch (error) {
      console.error('Error applying background blur:', error);
    } finally {
      if (effect !== VideoCallBackgroundEffect.background) {
        setIsApplying(false);
      }
      setApplyingEffect(null);
    }
  };

  useEffect(() => {
    if (!isSetupDone) {
      setIsBlurEnabled(false);
      setTransformDevice(null);
    }
  }, [isSetupDone]);

  useEffect(() => {
    if (!isBlurEnabled && transformDevice) {
      transformDevice.stop?.();
      setTransformDevice(null);
    }
  }, [isBlurEnabled]);

  const convertUrlToDataUrl = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      return { dataUrl, blob };
    } catch (e) {
      console.error('failed to fetch or convert image ', url, e);
    }
  };

  const loadImage = (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = '';
      img.onload = () => {
        resolve(img);
      };
      img.onerror = (e) => {
        console.error('background image load failed', img.src, e);
        reject(e);
      };
      img.src = dataUrl;
    });
  };

  const applyBackgroundReplacement = async (imageUrl) => {
    if (isApplying) return;
    try {
      setIsApplying(true);
      setApplyingEffect('replacement');
      if (!selectedVideoDevice || !audioVideo) {
        return;
      }
      if (!imageUrl) {
        return;
      }
      const { dataUrl, blob } = await convertUrlToDataUrl(imageUrl);
      const backgroundImage = await loadImage(dataUrl);
      if (isBlurEnabled) {
        await handleBlurToggle(false, VideoCallBackgroundEffect.background);
      }
      if (transformDevice?.stop) {
        await audioVideo.stopVideoInput();
        await transformDevice.stop();
      }
      const processor = await BackgroundReplacementVideoFrameProcessor.create(null, {
        image: backgroundImage,
        imageBlob: blob,
      });
      const newTransformDevice = new DefaultVideoTransformDevice(console, selectedVideoDevice, [
        processor,
      ]);
      await audioVideo.startVideoInput(newTransformDevice);
      audioVideo.startLocalVideoTile();
      setTransformDevice(newTransformDevice);
      setIsReplacementEnabled(true);
      setIsBlurEnabled(false);
      saveEffectForUser(VideoCallBackgroundEffect.background);
    } catch (error) {
      console.error('Error applying background replacement:', error);
    } finally {
      setIsApplying(false);
      setApplyingEffect(null);
    }
  };

  const revertBackground = async () => {
    try {
      setIsApplying(true);
      setApplyingEffect('revert');
      if (!audioVideo || !selectedVideoDevice) return;
      await audioVideo.stopVideoInput();
      if (transformDevice?.stop) {
        transformDevice.stop();
      }
      await audioVideo.startVideoInput(selectedVideoDevice);
      audioVideo.startLocalVideoTile();
      setTransformDevice(null);
      setIsReplacementEnabled(false);
      setIsBlurEnabled(false);
      setSelectedCustomBackground?.(null);
      saveEffectForUser(VideoCallBackgroundEffect.none);
    } catch (e) {
      console.error('Error reverting background:', e);
    } finally {
      setIsApplying(false);
      setApplyingEffect(null);
    }
  };

  useEffect(() => {
    if (isBlurEnabled) {
      lastEffectRef.current = VideoCallBackgroundEffect.blur;
    } else if (isReplacementEnabled && selectedCustomBackground) {
      lastEffectRef.current = 'replacement';
    } else {
      lastEffectRef.current = VideoCallBackgroundEffect.none;
    }
  }, [isBlurEnabled, isReplacementEnabled, selectedCustomBackground]);

  effectHandlersRef.current = {
    handleBlurToggle,
    applyBackgroundReplacement,
    revertBackground,
  };

  useEffect(() => {
    if (onProvideHandlers) {
      onProvideHandlers({
        handleBlurToggle: (...args) => effectHandlersRef.current.handleBlurToggle?.(...args),
        applyBackgroundReplacement: (...args) =>
          effectHandlersRef.current.applyBackgroundReplacement?.(...args),
        revertBackground: (...args) => effectHandlersRef.current.revertBackground?.(...args),
      });
    }
  }, [onProvideHandlers]);

  if (isBootstrapOnly) {
    return null;
  }

  return (
    <div className="videocall-settings-wrapper">
      <div className="settings-section video-settings">
        <div className="video-audio-call-settings-section">
          <div className="video-audio-call-settings-options-container">
            <label className="video-audio-call-settings-options-label">{trans('camera')}</label>
          </div>
          <div className="video-call-settings-options-container">
            <CustomDropdown
              className="options"
              overlayClassName="settings-custom-dropdown-width"
              menuItems={getOptions(videoDevices)}
              selectedKeys={[selectedVideoDevice]}
              menuProps={{ selectable: false }}
              disabled={!isSetupDone || !isVideoEnabled}
              handleChange={handleVideoDeviceChange}
            />
          </div>
        </div>
      </div>
      <div className="settings-section audio-settings">
        <div className="video-audio-call-settings-section">
          <div className="video-audio-call-settings-options-container">
            <label className="video-audio-call-settings-options-label">
              {trans('audio_output')}
            </label>
          </div>
          <div className="video-call-settings-options-container">
            <CustomDropdown
              className="options"
              overlayClassName="settings-custom-dropdown-width"
              menuItems={getOptions(audioOutDevices)}
              selectedKeys={[selectedAudioOutDevice]}
              menuProps={{ selectable: false }}
              disabled={!isSetupDone}
              handleChange={handleAudioOutputChange}
            />
          </div>
        </div>
        <div className="audio-level-container">
          <div className="audio-level-progress-bar">
            <div className="progress-bar-sound-bar-icon">
              {volumeLevel === 0 ? (
                <VolumeOffIcon className="volume-off-icon" onClick={() => setVolumeLevel(1)} />
              ) : (
                <VolumeIcon className="volume-icon" onClick={() => setVolumeLevel(0)} />
              )}
            </div>
            <ProgressBar
              className="volume-level-bar"
              adjustable={true}
              percent={volumeLevel * 100}
              onChange={(v) => setVolumeLevel(v / 100)}
            />
          </div>
          <div className="audio-level-value">
            <span className="audio-level-value-label">{trans('MUTE')}</span>
            <span className="volume-number">{Math.round(volumeLevel * 100)}</span>
            <span className="audio-level-value-label">{trans('MAX')}</span>
          </div>
        </div>
      </div>
      <div className="settings-section microphone-settings">
        <div className="video-audio-call-settings-section">
          <div className="video-audio-call-settings-options-container">
            <label className="video-audio-call-settings-options-label">{trans('MICROPHONE')}</label>
          </div>
          <div className="video-call-settings-options-container">
            <CustomDropdown
              className="options"
              overlayClassName="settings-custom-dropdown-width"
              menuItems={getOptions(audioInDevices).map((item) => ({
                ...item,
                label: item.renderLabel,
                rawLabel: item.label,
              }))}
              selectedKeys={[selectedAudioInDevice]}
              menuProps={{ selectable: false }}
              disabled={!isSetupDone || muted}
              handleChange={handleAudioInputChange}
            />
          </div>
        </div>
        <div className="audio-level-container">
          <div className="audio-level-progress-bar">
            <div className="progress-bar-sound-bar-icon">
              {volumeLevel === 0 ? (
                <MicOffIcon className="video-call-mic-icon" />
              ) : (
                <MicOnIcon className="video-call-mic-icon" />
              )}
            </div>
            <ProgressBar percent={audioInStrength} className="volume-level-bar" />
          </div>
        </div>
      </div>
      <div className="settings-section blur-background-settings">
        <div className="settings-section-blur-background-content-block">
          <div className="settings-section-blur-background-header">
            <span className="settings-section-blur-background-label">
              {trans('BLURBACKGROUND')}
            </span>
          </div>
          <div className="settings-section-blur-background-actions-container">
            <div
              className={`settings-section-blur-background-action ${
                selectedEffect === VideoCallBackgroundEffect.none
                  ? 'settings-section-blur-background-action-selected'
                  : ''
              } ${!isVideoEnabled || isApplying ? 'settings-section-blur-background-disabled' : ''}`}
              onClick={async () => {
                if (isApplying) return;
                setSelectedEffect(VideoCallBackgroundEffect.none);
                if (
                  !isVideoEnabled ||
                  selectedEffect === VideoCallBackgroundEffect.none ||
                  isApplying
                )
                  return;
                await revertBackground();
              }}
              style={{
                cursor:
                  isVideoEnabled && selectedEffect !== VideoCallBackgroundEffect.none && !isApplying
                    ? 'pointer'
                    : 'not-allowed',
              }}
            >
              <BlurBackgroundIcon className="settings-section-blur-background-icon" />
              <span className="settings-section-blur-background-action-label">
                {trans('NOEFFECT')}
              </span>
              {isNoneApplying && (
                <div className="settings-section-effect-applying-overlay">
                  <span className="settings-section-effect-applying-overlay-spinner" />
                </div>
              )}
            </div>
            <div
              className={`settings-section-blur-background-action ${
                selectedEffect === VideoCallBackgroundEffect.blur
                  ? 'settings-section-blur-background-action-selected'
                  : ''
              } ${!isVideoEnabled || isApplying ? 'settings-section-blur-background-disabled' : ''}`}
              onClick={async () => {
                if (isApplying) return;
                setSelectedEffect(VideoCallBackgroundEffect.blur);
                if (
                  !isVideoEnabled ||
                  selectedEffect === VideoCallBackgroundEffect.blur ||
                  isApplying
                )
                  return;
                await handleBlurToggle(true);
              }}
              style={{
                cursor:
                  isVideoEnabled && selectedEffect !== VideoCallBackgroundEffect.blur && !isApplying
                    ? 'pointer'
                    : 'not-allowed',
              }}
            >
              <BlurBackgroundIcon className="settings-section-blur-background-icon" />
              <span className="settings-section-blur-background-action-label">{trans('BLUR')}</span>
              {isBlurApplying && (
                <div className="settings-section-effect-applying-overlay">
                  <span className="settings-section-effect-applying-overlay-spinner" />
                </div>
              )}
            </div>
          </div>
        </div>
        {!blurSupported && (
          <span className="settings-section-blur-background-warning">
            {trans('YOURBROWSERDOESNOTSUPPORTBACKGROUNDBLUR')}
          </span>
        )}
        {shouldShowProviderBackground && (
          <div
            className={`settings-section-custom-background ${
              isCustomBackgroundSelected ? 'settings-section-custom-background-block-selected' : ''
            }`}
          >
            <div className="settings-section-custom-background-label-block">
              <span className="settings-section-custom-background-label">
                {trans('PROVIDERBACKGROUND')}
              </span>
            </div>
            <div
              className={`settings-section-custom-background-block ${
                !isVideoEnabled || isApplying
                  ? 'settings-section-custom-background-block-disabled'
                  : ''
              }`}
              style={{
                cursor:
                  !isVideoEnabled ||
                  isApplying ||
                  (selectedEffect === VideoCallBackgroundEffect.background && isApplying === false)
                    ? 'not-allowed'
                    : 'pointer',
              }}
              onClick={async () => {
                if (isApplying) return;
                setSelectedEffect(VideoCallBackgroundEffect.background);
                if (
                  !isVideoEnabled ||
                  isApplying ||
                  selectedEffect === VideoCallBackgroundEffect.background
                )
                  return;
                //second click removes background
                // if(isCustomBackgroundSelected) {
                //   await revertBackground();
                //   return;
                // }
                await applyBackgroundReplacement(customBackgroundImage);
                setSelectedCustomBackground(customBackgroundImage);
              }}
            >
              {customBackgroundImage ? (
                <div
                  className={`settings-section-custom-background-image-container-block ${shouldShowProviderBackground ? 'settings-section-custom-background-image-container-block-selected' : ''}`}
                >
                  <img
                    src={customBackgroundImage}
                    alt="Custom Background"
                    className="custom-background-image"
                  />
                  {isCustomBackgroundSelected && isVideoEnabled && (
                    <div className="settings-section-selected-custom-background-tag">
                      <div className="settings-section-selected-custom-background-tag-block">
                        <NewUpdateIcon className="settings-section-selected-custom-background-selected-icon" />
                        <span className="settings-section-selected-custom-background-label">
                          {trans('SELECTED')}
                        </span>
                      </div>
                    </div>
                  )}
                  {isBackgroundApplying && isVideoEnabled && (
                    <div className="settings-section-selected-custom-background-tag">
                      <div className="settings-section-selected-custom-background-tag-block">
                        <span className="settings-section-effect-applying-spinner" />
                        <span className="settings-section-selected-custom-background-label">
                          {trans('APPLYING_BACKGROUND')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <span>{trans('NOBACKGROUNDIMAGEFOUND')}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

VideoCallSettings.propTypes = {
  isSetupDone: PropTypes.bool,
  showSelfView: PropTypes.bool,
  setShowSelfView: PropTypes.func,
  isBootstrapOnly: PropTypes.bool,
};

export default VideoCallSettings;
